<template>
  <b-modal
    id="modal-package-registration-bulk-import"
    :title="!submitted ? 'Đăng ký theo doanh nghiệp' : 'Đã gửi yêu cầu'"
    size="md"
    centered
    hide-footer
    @hidden="resetModal"
  >
    <b-overlay :show="loading">
      <form v-if="!submitted" @submit.prevent="handleSubmit">
        <div class="mb-4">
          <label class="font-weight-bold">Chọn chiến dịch</label>
          <select v-model="selectedCampaignId" class="form-control" @change="onCampaignChange">
            <option :value="null" disabled>-- Chọn chiến dịch --</option>
            <option v-for="c in campaigns" :key="c.id" :value="c.id">
              {{ c.name }}
            </option>
          </select>
        </div>

        <div class="mb-4">
          <label class="font-weight-bold">Chọn gói dịch vụ</label>
          <select v-model="selectedPackageId" class="form-control">
            <option :value="null" disabled>-- Chọn gói --</option>
            <option v-for="pkg in packages" :key="pkg.id" :value="pkg.id">
              {{ pkg.name }}
            </option>
          </select>
        </div>

        <div v-if="selectedCampaignId" class="mb-1">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <label class="font-weight-bold mb-0">
              Danh sách nhân viên
              <small class="text-muted d-block font-weight-normal">Đã chọn {{ selectedCustomerIds.size }}/{{ customers.length }}</small>
            </label>
            <div class="custom-control custom-checkbox">
              <input
                type="checkbox"
                class="custom-control-input"
                id="cb-select-all-customers"
                :checked="allSelected"
                @change="toggleSelectAll($event.target.checked)"
              />
              <label class="custom-control-label" for="cb-select-all-customers">Chọn tất cả</label>
            </div>
          </div>

          <div v-if="customersLoading" class="text-muted small">Đang tải danh sách nhân viên...</div>
          <div v-else-if="customers.length === 0" class="text-muted small">Chiến dịch này chưa có nhân viên nào</div>
          <div v-else class="border rounded" style="max-height: 260px; overflow-y: auto;">
            <table class="table table-sm mb-0">
              <thead>
                <tr>
                  <th style="width:32px;"></th>
                  <th>Họ tên</th>
                  <th>SĐT</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="c in customers" :key="c.id">
                  <td>
                    <div class="custom-control custom-checkbox">
                      <input
                        type="checkbox"
                        class="custom-control-input"
                        :id="'cb-customer-' + c.id"
                        :checked="selectedCustomerIds.has(c.id)"
                        @change="toggleOne(c.id)"
                      />
                      <label class="custom-control-label" :for="'cb-customer-' + c.id"></label>
                    </div>
                  </td>
                  <td>{{ c.fullName || '—' }}</td>
                  <td>{{ c.phone || '—' }}</td>
                  <td>{{ c.email || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="d-flex justify-content-end align-items-center mt-4">
          <b-button class="btn mr-2" type="button" @click="onClickCancel">Hủy</b-button>
          <b-button class="btn btn-success ml-3" type="submit" :disabled="disableSubmit || loading">
            <i class="fas fa-paper-plane mr-1" />
            Đăng ký
          </b-button>
        </div>
      </form>

      <div v-else class="text-center py-4">
        <i class="fas fa-check-circle text-success mb-3" style="font-size:2.5rem;" />
        <p class="mb-1">
          Đã gửi <b>{{ submittedTotal }}</b> nhân viên vào hàng đợi xử lý.
        </p>
        <p class="text-muted small mb-4">
          Việc tạo gói chạy ở nền. Kết quả (thành công/thất bại kèm lý do) sẽ được gửi qua email
          của bạn kèm file Excel chi tiết khi xử lý xong toàn bộ.
        </p>
        <b-button class="btn btn-success" type="button" @click="onClickCancel">Đóng</b-button>
      </div>
    </b-overlay>
  </b-modal>
</template>

<script>
export default {
  name: 'PackageRegistrationBulkImport',
  data() {
    return {
      loading: false,
      packages: [],
      selectedPackageId: null,
      campaigns: [],
      selectedCampaignId: null,
      customers: [],
      customersLoading: false,
      selectedCustomerIds: new Set(),
      submitted: false,
      submittedTotal: 0,
    };
  },
  computed: {
    disableSubmit() {
      return !this.selectedCampaignId || !this.selectedPackageId || this.selectedCustomerIds.size === 0;
    },
    allSelected() {
      return this.customers.length > 0 && this.selectedCustomerIds.size === this.customers.length;
    },
  },
  methods: {
    async loadPackages() {
      try {
        const { data } = await this.$api.get('SalePackage?status=1&size=1000');
        if (Array.isArray(data)) this.packages = data;
        else if (Array.isArray(data?.items)) this.packages = data.items;
        else if (Array.isArray(data?.data)) this.packages = data.data;
        else this.packages = [];
      } catch (e) {
        this.$toastr.e('Không thể tải danh sách gói');
      }
    },

    async loadCampaigns() {
      try {
        const res = await this.$api.get('BcbCampaign', { params: { page: 1, size: 200 } });
        const raw = res?.data ?? res;
        this.campaigns = raw?.items ?? raw?.data?.items ?? (Array.isArray(raw) ? raw : []);
      } catch (e) {
        this.$toastr.e('Không thể tải danh sách chiến dịch');
      }
    },

    async onCampaignChange() {
      this.customers = [];
      this.selectedCustomerIds = new Set();
      if (!this.selectedCampaignId) return;
      this.customersLoading = true;
      try {
        const res = await this.$api.get('BcbCampaignCustomer', {
          params: { campaignId: this.selectedCampaignId, page: 1, size: 1000 },
        });
        const raw = res?.data ?? res;
        this.customers = raw?.items ?? raw?.data?.items ?? (Array.isArray(raw) ? raw : []);
        this.selectedCustomerIds = new Set(this.customers.map((c) => c.id));
      } catch (e) {
        this.$toastr.e('Không thể tải danh sách nhân viên của chiến dịch');
      } finally {
        this.customersLoading = false;
      }
    },

    toggleOne(id) {
      const next = new Set(this.selectedCustomerIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      this.selectedCustomerIds = next;
    },

    toggleSelectAll(checked) {
      this.selectedCustomerIds = checked ? new Set(this.customers.map((c) => c.id)) : new Set();
    },

    async handleSubmit() {
      if (this.disableSubmit) return;
      this.loading = true;
      try {
        const { data } = await this.$api.post('/UserBundleOrders/register-by-campaign', {
          campaignId: this.selectedCampaignId,
          salePackageId: this.selectedPackageId,
          customerIds: Array.from(this.selectedCustomerIds),
        });

        this.submittedTotal = data?.total ?? this.selectedCustomerIds.size;
        this.submitted = true;
      } catch (err) {
        this.$toastr.e('Gửi yêu cầu đăng ký thất bại');
      } finally {
        this.loading = false;
      }
    },

    resetModal() {
      if (this.submitted) this.$emit('success');
      this.selectedPackageId = null;
      this.selectedCampaignId = null;
      this.customers = [];
      this.selectedCustomerIds = new Set();
      this.submitted = false;
      this.submittedTotal = 0;
    },

    onClickCancel() {
      this.$bvModal.hide('modal-package-registration-bulk-import');
    },
  },
  created() {
    this.loadPackages();
    this.loadCampaigns();
  },
};
</script>
